<template>
    <div class="card mt-2">
        <div class="card-header d-flex justify-content-between align-items-start">
            <h5 class="card-title">{{ title }}</h5>
            <button :id="'add' + title.replaceAll(' ','') + 'Button'" type="button" class="btn btn-outline-secondary" v-on:click="addContact">Add</button>
        </div>
        <ul class="list-group list-group-flush">
            <li class="list-group-item d-flex justify-content-between align-items-start" v-for="contact in contacts" :key="contact.id">
                <input type="hidden" v-bind:name="serializedValueName" v-bind:value="contact.serialized" />
                <div class="p-0 m-0 flex-grow-1">
                    <div class="font-weight-bold">{{ contact.name }}</div>
                    <div v-if="(contact.email != null)">{{ contact.email }}</div>
                    <div v-if="(contact.phone != null)">{{ contact.phone }}</div>
                    <div v-if="(contact.address1 != null)">{{ contact.address1 + (contact.address2 ? (', ' + contact.address2) : '') }}</div>
                    <div v-if="($config.enableRegionStateOptions && contact.suburb != null)">{{ contact.suburb + (contact.state ? (' ' + contact.state) : '') + (contact.postcode ? (' ' + contact.postcode) : '') + (contact.country ? (' ' + contact.country) : '') }}</div>
                    <div v-if="(!$config.enableRegionStateOptions && contact.suburb != null)">{{ contact.suburb + (contact.postcode ? (' ' + contact.postcode) : '') + (contact.country ? (' ' + contact.country) : '') }}</div>
                    <div v-if="(contact.contactTypes != null) && (contact.contactTypes.length > 0)">
                        <b-badge v-for="contactType in contact.contactTypes" :key="contactType" class="mr-1">{{ contactTypeDisplayName(contactType) }}</b-badge>
                    </div>
                </div>
                <div class="btn-group" role="group" aria-label="Action buttons">
                    <button type="button" class="btn btn-outline-secondary" v-on:click="editContact(contact)">Edit</button>
                    <button type="button" class="btn btn-outline-secondary" v-on:click="removeContact(contact)">Remove</button>
                </div>
            </li>
        </ul>
        <validation-observer v-slot="{ passes }" slim>
            <b-modal id="contactEditDialog"
                     size="lg"
                     centered
                     v-model="contactEditDialogShown"
                     v-bind:title="detailsDialogTitle"
                     v-on:ok.prevent="passes(createOrUpdateContact)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <b-form-group label="Title"
                                  label-for="contactTitle"
                                  class="col-md-2">
                        <b-form-input name="contactTitle"
                                      autofocus
                                      v-model="currentContact.title">
                        </b-form-input>
                    </b-form-group>
                    <validation-provider :rules="`${!currentContact.lastName ? 'required' : ''}`" name="Given Names" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Given Names"
                                      label-for="contactGivenNames"
                                      class="col-md-5">
                            <b-form-input name="contactGivenNames"
                                          v-model="currentContact.givenNames"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${!currentContact.givenNames ? 'required' : ''}`" name="Last Name" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Last Name"
                                      label-for="contactLastName"
                                      class="col-md-5">
                            <b-form-input name="contactLastName"
                                          v-model="currentContact.lastName"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
                <b-form-row>
                    <validation-provider rules="required|email" name="Email" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Email"
                                      label-for="contactEmail"
                                        class="col-md-6">
                            <b-form-input name="contactEmail"
                                          v-model="currentContact.email"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${isContactPropertyMandatory('phone') ? 'required' : ''}`" name="Phone" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Phone"
                                      label-for="contactPhone"
                                      class="col-md-6">
                            <vue-tel-input name="contactPhone"
                                           ref="contactPhone"
                                           v-model="currentContact.phone"
                                           :defaultCountry="defaultCountryCode"
                                           :mode="phoneMode"
                                           :onlyCountries="countryList"
                                           :preferredCountries="preferredCountyList"
                                           v-on:country-changed="phoneCountryChanged"
                                           :state="errors[0] ? false : null"
                                           class="form-control d-flex">
                            </vue-tel-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${isContactPropertyMandatory('address') ? 'required' : ''}`" name="Address" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Address"
                                      label-for="contactAddress1"
                                      class="col-12">
                            <b-form-input name="contactAddress1"
                                          v-model="currentContact.address1"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <b-form-group class="col-12">
                        <b-form-input name="contactAddress2"
                                      v-model="currentContact.address2">
                        </b-form-input>
                    </b-form-group>
                    <validation-provider :rules="`${isContactPropertyMandatory('suburb') ? 'required' : ''}`" name="Suburb" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Suburb"
                                      label-for="contactSuburb"
                                      class="col-md-4">
                            <b-form-input name="contactSuburb"
                                          v-model="currentContact.suburb"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider v-if="$config.enableRegionStateOptions"
                                         :rules="`${isContactPropertyMandatory('state') ? 'required' : ''}`" name="State" v-slot="{ valid, errors }" slim>
                        <b-form-group v-if="$config.enableRegionStateOptions"
                                      label="State"
                                      label-for="contactState"
                                      class="col-md-2">
                            <select2 name="contactState"
                                     v-model="currentContact.state"
                                     :options="states"
                                     tags
                                     in-b-modal
                                     :state="errors[0] ? false : null">
                            </select2>

                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${isContactPropertyMandatory('postcode') ? 'required' : ''}`" name="Postcode" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Postcode"
                                      label-for="contactPostcode"
                                      class="col-md-2">
                            <b-form-input name="contactPostcode"
                                          v-model="currentContact.postcode"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider :rules="`${isContactPropertyMandatory('country') ? 'required' : ''}`" name="Country" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Country"
                                      label-for="contactCountry"
                                      class="col-md-4">
                            <b-form-input name="contactCountry"
                                          v-model="currentContact.country"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
                <b-form-row>
                    <b-form-group label="Contact Type"
                                  label-for="contactTypes"
                                  class="col"
                                  v-if="(contactTypes != null) && (contactTypes.length > 0)">
                        <select2 name="contactTypes"
                                 v-model="currentContact.contactTypes"
                                 :options="contactTypes"
                                 multiple
                                 in-b-modal>
                        </select2>
                    </b-form-group>
                </b-form-row>
            </b-modal>
        </validation-observer>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required, email } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('email', email);
    localize('en', en);

    import VueTelInput from 'vue-tel-input';
    import 'vue-tel-input/dist/vue-tel-input.css';

    import Select2 from './Select2';

    import ErrorsMixin from '../mixins/ErrorsMixin';
    
    Vue.use(BootstrapVue);
    Vue.use(VueTelInput);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('select2', Select2);

    export default {
        name: 'ContactManagement',
        mixins: [ErrorsMixin],
        props: {
            apiControllerUrl: {
                type: String,
                required: true
            },
            refDataApiControllerUrl: {
                type: String,
                required: true
            },
            title: {
                type: String,
                default: 'Contacts'
            },
            serializedValueName: {
                type: String,
                required: true
            },
            contacts: {
                type: Array,
                default: null
            },
            mandatoryContactProperties: {
                type: Array,
                default: null
            },
            contactTypes: {
                type: Array,
                default: null
            }
        },
        data: function () {
            return {
                contactEditDialogShown: false,
                currentContact: { id: null, title: null, givenNames: null, lastName: null, email: null, phone: null, address1: null, address2: null, postcode: null, suburb: null, state: null, country: null, contactTypes: null },
                currentErrors: [],
                states: [],
                initialCountry: null,
                countryList: [],
                preferredCountyList: [],
                defaultCountryCode: null,
            }
        },
        computed: {
            detailsDialogTitle: function () {
                return this.title + ' Details';
            },
            showErrors: function () {
                return this.currentErrors.length > 0;
            },
            phoneMode: function () {
                if (!this.currentContact.id) {
                    return "international";
                }
                else if (!this.currentContact.phone || this.currentContact.phone == '' || !this.currentContact.phone?.startsWith("0")) {
                    return "international";
                }
                else { 
                    return "national";
                }
            }
        },
        methods: {
            isContactPropertyMandatory: function (propertyName) {
                if (this.mandatoryContactProperties == null) return false;
                return (this.mandatoryContactProperties.indexOf(propertyName) >= 0);
            },
            contactTypeDisplayName: function (contactTypeValue) {
                if (this.contactTypes == null) return contactTypeValue;
                var matchingContactType = this.contactTypes.find(e => {
                    return (e.value == contactTypeValue);
                });
                if (matchingContactType == null) return contactTypeValue;
                return matchingContactType.text;
            },
            addContact: function () {
                this.currentContact = { id: null, title: null, givenNames: null, lastName: null, email: null, phone: null, address1: null, address2: null, postcode: null, suburb: null, state: null, country: this.initialCountry, contactTypes: null };
                this.currentErrors = [];
                this.contactEditDialogShown = true;
            },
            editContact: function (contact) {
                this.currentContact = JSON.parse(JSON.stringify(contact));
                this.currentErrors = [];
                this.contactEditDialogShown = true;
            },
            removeContact: function (contact) {
                var contactIndex = this.contacts.indexOf(contact);
                this.contacts.splice(contactIndex, 1);
            },
            createOrUpdateContact: function () {
                var data = {
                    serializedContacts: [],
                    contact: this.currentContact
                }               

                this.currentErrors = [];
                this.contacts.forEach(cc => data.serializedContacts.push(cc.serialized));
                //console.log(JSON.stringify(data));
                axios
                    .post(this.apiControllerUrl, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(response => {
                        this.contacts = response.data
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.contactEditDialogShown = false;
                        }
                    })
            },
            phoneCountryChanged: function (country) {  
                var selectedCountryCode = country.iso2.toLowerCase();
                var defaultCountryCode = this.defaultCountryCode.toLowerCase();

                if (this.currentContact.phone?.startsWith("0") && selectedCountryCode != defaultCountryCode) {
                    this.currentContact.phone = '+' + country.dialCode + ' ' + this.currentContact.phone.substring(1);                    
                }
            }
        },
        created: function () {
            var url = this.refDataApiControllerUrl;
            if (this.$config.enableRegionStateOptions) {
                if (!url.endsWith("/"))
                    url += "/";
                url += "list/Contact.State.Choices"
                axios
                    .get(url)
                    .then(response => {
                        this.states = response.data
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            }
            url = this.refDataApiControllerUrl;
            if (!url.endsWith("/"))
                url += "/";
            url += "value/Contact.Country.InitialValue"
            axios
                .get(url)
                .then(response => {
                    this.initialCountry = response.data
                })
                .catch(ex => {
                    var currentErrors = this.handleAxiosException(ex);
                    this.showErrorsToast(currentErrors);
                });

            url = this.refDataApiControllerUrl;
            if (!url.endsWith("/"))
                url += "/";
            url += "list/Contact.CountryCode.Choices"
            axios
                .get(url)
                .then(response => {
                    response.data.forEach(obj => (this.countryList.push(obj.value)));
                })
                .catch(ex => {
                    var currentErrors = this.handleAxiosException(ex);
                    this.showErrorsToast(currentErrors);
                });

            url = this.refDataApiControllerUrl;
            if (!url.endsWith("/"))
                url += "/";
            url += "list/Contact.PreferredCountryCode.Choices"
            axios
                .get(url)
                .then(response => {
                    response.data.forEach(obj => (this.preferredCountyList.push(obj.value)));
                })
                .catch(ex => {
                    var currentErrors = this.handleAxiosException(ex);
                    this.showErrorsToast(currentErrors);
                });

            url = this.refDataApiControllerUrl;
            if (!url.endsWith("/"))
                url += "/";
            url += "value/Contact.CountryCode.DefaultValue"
            axios
                .get(url)
                .then(response => {
                    this.defaultCountryCode = response.data
                })
                .catch(ex => {
                    var currentErrors = this.handleAxiosException(ex);
                    this.showErrorsToast(currentErrors);
                });
        },
    }
</script>